import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Check, X } from 'lucide-react';

export interface OnboardingStep {
  key: string;
  label: string;
  done: boolean;
  /** Route or same-page anchor the step's action points at. */
  href: string;
  hint: string;
}

export function tillKitPrintedKey(shopId: string): string {
  return `bitbrew_tillkit_printed_${shopId}`;
}
export function onboardingDismissedKey(shopId: string): string {
  return `bitbrew_onboarding_dismissed_${shopId}`;
}

/**
 * Pure derivation so the completion rules are unit-testable. `shop` is the
 * Supabase shop row (SHOP_PUBLIC_COLUMNS shape); squareConnected is null
 * while the status call is still in flight (treated as not-done).
 */
export function deriveOnboardingSteps(
  shop: {
    image_url?: string | null;
    beans?: unknown[] | null;
    stamp_icon?: string | null;
  } | null,
  squareConnected: boolean | null,
  tillKitPrinted: boolean,
): OnboardingStep[] {
  return [
    {
      key: 'square',
      label: 'CONNECT SQUARE',
      done: squareConnected === true,
      href: '#square-pos',
      hint: 'Till payments auto-confirm stamps',
    },
    {
      key: 'logo',
      label: 'UPLOAD YOUR LOGO',
      done: !!shop?.image_url,
      href: '/shops/settings',
      hint: 'Shown on your card in every customer app',
    },
    {
      key: 'beans',
      label: 'ADD WHAT YOU POUR',
      done: Array.isArray(shop?.beans) && shop!.beans!.length > 0,
      href: '/shops/settings',
      hint: 'Your beans show on the shop page',
    },
    {
      key: 'stamp',
      label: 'PICK A STAMP ICON',
      done: !!shop?.stamp_icon && shop.stamp_icon !== 'default',
      href: '/shops/settings',
      hint: 'The mark customers collect',
    },
    {
      key: 'tillkit',
      label: 'PRINT YOUR TILL KIT',
      done: tillKitPrinted,
      href: '/shops/till-kit',
      hint: 'QR card for the counter',
    },
  ];
}

interface OnboardingChecklistProps {
  shopId: string;
  shop: Parameters<typeof deriveOnboardingSteps>[0];
  squareConnected: boolean | null;
}

/**
 * First-run setup checklist on the owner dashboard. Disappears on its own
 * once every step is done; dismissible early via [X] (per-shop, sticky).
 */
export default function OnboardingChecklist({ shopId, shop, squareConnected }: OnboardingChecklistProps) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(onboardingDismissedKey(shopId)) === '1',
  );
  const tillKitPrinted = localStorage.getItem(tillKitPrintedKey(shopId)) === '1';
  const steps = deriveOnboardingSteps(shop, squareConnected, tillKitPrinted);
  const doneCount = steps.filter((s) => s.done).length;

  if (dismissed || doneCount === steps.length) return null;

  function dismiss() {
    localStorage.setItem(onboardingDismissedKey(shopId), '1');
    setDismissed(true);
  }

  return (
    <div className="bg-white rounded-[10px] p-[18px] mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm md:text-base text-primary flex items-center gap-2 font-grotesk font-bold">
          <ClipboardList size={20} className="text-gold-ink" /> SHOP SETUP · {doneCount}/{steps.length}
        </h2>
        <button
          onClick={dismiss}
          aria-label="Dismiss setup checklist"
          className="text-body-grey hover:text-primary p-2 -m-2"
        >
          <X size={14} />
        </button>
      </div>
      {/* Segment progress bar */}
      <div className="flex gap-1 mb-4">
        {steps.map((s) => (
          <div
            key={s.key}
            className={`h-1.5 flex-1 rounded-full ${s.done ? 'bg-[#2f9440]' : 'bg-accent-alt'}`}
          />
        ))}
      </div>
      <ul className="space-y-2">
        {steps.map((step) => (
          <li key={step.key}>
            {step.done ? (
              <div className="flex items-center gap-3 p-2 opacity-60">
                <span className="w-5 h-5 shrink-0 rounded-full border-2 border-[#2f9440] text-[#2f9440] flex items-center justify-center">
                  <Check size={12} />
                </span>
                <span className="font-grotesk font-medium text-[13px] text-[#2f9440] line-through">{step.label}</span>
              </div>
            ) : step.href.startsWith('#') ? (
              <a href={step.href} className="flex items-center gap-3 p-2 hover:bg-tab-inactive rounded-[10px] transition-colors group">
                <span className="w-5 h-5 shrink-0 rounded-full border-2 border-hairline group-hover:border-gold-ink" />
                <span className="font-grotesk font-bold text-[13px] text-primary">{step.label}</span>
                <span className="font-grotesk font-medium text-[12px] text-body-grey hidden sm:inline">— {step.hint}</span>
              </a>
            ) : (
              <Link to={step.href} className="flex items-center gap-3 p-2 hover:bg-tab-inactive rounded-[10px] transition-colors group">
                <span className="w-5 h-5 shrink-0 rounded-full border-2 border-hairline group-hover:border-gold-ink" />
                <span className="font-grotesk font-bold text-[13px] text-primary">{step.label}</span>
                <span className="font-grotesk font-medium text-[12px] text-body-grey hidden sm:inline">— {step.hint}</span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
